import qs from 'qs';
import { getUserState } from '@carvana/core-auth';
import UserAccountService from '@services/UserAccountService';
import sha256 from '@utilities/sha256';
import { closeCurrentModal } from '@Header/ducks/operations';
import { ACCOUNT_MODAL_TABS } from '../../../../constants';
import * as actions from './actions';
import AccountService from '../../../../services/AccountService';
import GoogleTagManagerClient from '../../../../services/GoogleTagManagerClient';
import ApplicationSettings from '../../../../services/ApplicationSettings';
import { isOnReferrals, removeQueryParam } from '../../../../utilities';
import HeaderApi from '../../../../services/HeaderApi';
import { getAccountCreationCallerId } from './selectors';

export const goToSignInTab = () => dispatch => {
  dispatch(actions.signInError({ message: '' }));
  dispatch(actions.setSelectedTab(ACCOUNT_MODAL_TABS.signIn));
};

export const goToCreateAccountTab = () => dispatch => {
  dispatch(actions.setSelectedTab(ACCOUNT_MODAL_TABS.createAccount));
};

export const goToForgotPasswordTab = () => (dispatch, getState) => {
  const state = getState();
  if (state.accountModal.forgotPassword.hasResetPassword) dispatch(actions.clearForgotPasswordState());
  dispatch(actions.setSelectedTab(ACCOUNT_MODAL_TABS.forgotPassword));
};

export const goToResetPasswordTab = () => (dispatch, getState) => {
  const state = getState();
  if (state.accountModal.resetPassword.hasResetPassword) dispatch(actions.clearResetPasswordState());
  dispatch(actions.setSelectedTab(ACCOUNT_MODAL_TABS.resetPassword));

  if (!state.accountModal.resetPassword.oneUseToken) {
    const { oneUseToken } = qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    });
    dispatch(actions.setResetPasswordToken(oneUseToken));
  }
};

export const clearCreateAccountErrors = actions.clearCreateUserError;

const shouldUseNewLogin = async () => {
  let useNewLogin = false;
  try {
    const { CoreNewLogin } = await HeaderApi.fetchAppSettings(['CoreNewLogin']);
    useNewLogin = CoreNewLogin == 'true';
  } catch (error) {
    useNewLogin = false;
  }

  return useNewLogin;
};

export const signUserIn = (
  userCredentials,
  { onSuccess, onError } = {},
  gtmPayload
) => async dispatch => {
  dispatch(actions.signInIsLoading());
  dispatch(actions.setTemporaryMessage(''));
  dispatch(actions.setAccountModalEmail(userCredentials.EmailAddress));

  const useNewLogin = await shouldUseNewLogin();

  if (useNewLogin) {
    return AccountService.logUserInUsingNewLogin(userCredentials)
      .then(async res => {
        const { data } = res;
        if (gtmPayload && data.content.userId) {
          const payload = { ...gtmPayload, uid: data.content.userId };
          GoogleTagManagerClient.trackEvent(payload);
        }

        if (onSuccess) return onSuccess({ responseURL: '' });

        if (isOnReferrals()) return refreshHeader();

        const urlParams = new URLSearchParams(window.location.search);

        //NOTE --  adding "returnUrl" query parameter with value will redirect

        const returnUrl =
          urlParams.get('returnUrl') ?? urlParams.get('ReturnUrl');
        if (returnUrl) return redirectOnLogin(returnUrl);

        const purchaseDetails = await HeaderApi.fetchHeaderWithAuth(
          'purchase/details'
        );

        const { vehicleId } = purchaseDetails || {};
        const isReserved =
          purchaseDetails?.orderState === 'Reserved' && vehicleId;
        const isActivePurchase = !isReserved && vehicleId;

        if (isReserved) {
          return redirectOnLogin(`/vehicle/${vehicleId}`);
        }

        if (isActivePurchase) {
          return redirectOnLogin(
            `/purchase/dashboard?stock-number=${purchaseDetails.stockNumber}`
          );
        }

        const postSaleDetails = await HeaderApi.fetchHeaderWithAuthV2(
          'postsale/details'
        );

        if (postSaleDetails?.IsUserInPostSale) {
          return redirectOnLogin('/post-sale-dashboard');
        }

        return refreshHeader();
      })
      .catch(error => {
        try {
          const { status, data } = error.response;
          const errorMessage =
            status === 400
              ? 'Email address and/or password combination is incorrect'
              : data.errorMessage || error.message;
          dispatch(
            actions.signInError({
              message: errorMessage
            })
          );
        } catch (ex) {
          dispatch(
            actions.signInError({
              message:
                'There was a problem processing the error from the server'
            })
          );
        }

        if (onError) onError({ error });
        return null;
      });
  } else {
    return AccountService.logUserIn(userCredentials)
      .then(res => {
        const {
          request: { responseURL }
        } = res;
        const dotComBaseUrl = ApplicationSettings.getApplicationSetting(
          'dotComBaseUrl'
        );
        const areUserCredentialsWrong = /\/account\/login/.test(responseURL);
        const hasUrlError =
          responseURL === `${dotComBaseUrl}/error` ||
          typeof responseURL === 'undefined';
        const redirectLocation = hasUrlError
          ? `${dotComBaseUrl}/account-ui/personal-details`
          : responseURL;

        if (areUserCredentialsWrong) {
          const errorMessage =
            'Email address and/or password combination is incorrect';
          dispatch(
            actions.signInError({
              message: errorMessage
            })
          );
          throw new Error(errorMessage);
        }

        if (gtmPayload) {
          const { jwt } = getUserState();
          const payload = { ...gtmPayload, uid: jwt?.user_id };
          GoogleTagManagerClient.trackEvent(payload);
        }

        if (onSuccess) return onSuccess({ responseURL });

        if (isOnReferrals()) return refreshHeader();

        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl =
          urlParams.get('returnUrl') ?? urlParams.get('ReturnUrl');
        if (returnUrl) return redirectOnLogin(returnUrl);

        const isDefaultRedirect =
          redirectLocation ===
          `${dotComBaseUrl}/account-ui/personal-details?e=login`;
        if (isDefaultRedirect) return refreshHeader();

        return redirectOnLogin(redirectLocation);
      })
      .catch(error => {
        console.error('login error:', error);

        if (onError) onError({ error });

        return null;
      });
  }
};

export const createUserAccount = (
  userCredentials,
  { onSuccess, onError },
  isInExperimentBucket
) => (dispatch, getState) => {
  const {
    email,
    password,
    phoneNumber,
    confirmPassword,
    zip5,
    firstName,
    lastName
  } = userCredentials;
  const partnerId = window.localStorage.getItem('PartnerId');
  const state = getState();
  const payload = {
    email,
    phoneNumber,
    password,
    confirmPassword: isInExperimentBucket ? password : confirmPassword,
    firstName,
    lastName,
    deliveryZip: zip5,
    isAgreeToTermsAndConditions: true,
    callerIdentifier: getAccountCreationCallerId(state),
    partnerId
  };
  dispatch(actions.createUserIsLoading());
  dispatch(actions.setAccountModalEmail(email));

  return AccountService.createAccount(payload)
    .then(() => {
      dispatch(actions.createUserSuccess());
      dispatch(closeCurrentModal());

      const { deliveryZip, email } = payload;
      const gtmPayload = {
        event: 'core-header-account-created',
        zip5: deliveryZip,
        hashedEmailAddress: sha256(email).toString()
      };

      return dispatch(
        signUserIn(
          { EmailAddress: email, Password: password },
          { onSuccess, onError },
          gtmPayload
        )
      );
    })
    .catch(error => {
      console.error(error);
      dispatch(actions.createUserError(error.response.data));

      if (onError) onError({ error });

      return null;
    });
};

export const forgotPassword = ({ email }) => dispatch => {
  const payload = { email };

  dispatch(actions.forgotPasswordIsLoading());
  return UserAccountService.forgotPassword(payload)
    .then(() => {
      dispatch(actions.forgotPasswordSuccess());
    })
    .catch(error => {
      console.error(error);
      dispatch(actions.forgotPasswordError(error));
    });
};

export const resetPassword = ({ password, oneUseToken }) => dispatch => {
  const payload = {
    token: oneUseToken,
    newPassword: password
  };

  dispatch(actions.resetPasswordIsLoading());
  return UserAccountService.resetPassword(payload)
    .then(() => {
      dispatch(actions.resetPasswordSuccess());
      const successMessage = 'Successfully changed password.';
      dispatch(actions.setTemporaryMessage(successMessage));
      dispatch(goToSignInTab());
    })
    .catch(error => {
      console.error(error);
      dispatch(actions.resetPasswordError(error));
    });
};

function redirectOnLogin(redirectUrl) {   
  window.location.href = redirectUrl;
}

export const refreshHeader = () => {
  removeQueryParam('openAccountModal');
  window.location.reload();
};
